import { ref } from 'vue'
import type { Ref } from 'vue'

export type GalleryToastKind = 'success' | 'error' | 'info'

export interface GalleryToast {
  id: number
  kind: GalleryToastKind
  message: string
  actionLabel: string | null
  onAction: (() => void) | null
}

export interface GalleryToastOptions {
  kind?: GalleryToastKind
  actionLabel?: string
  onAction?: () => void
  /** Milliseconds before the toast disappears. */
  durationMs?: number
}

// Module-level so the toasts survive page navigation, like the upload manager.
const toasts = ref<GalleryToast[]>([])
let seq = 0

function dismiss(id: number): void {
  toasts.value = toasts.value.filter((t) => t.id !== id)
}

function push(message: string, options: GalleryToastOptions = {}): number {
  seq += 1
  const id = seq
  toasts.value = [
    ...toasts.value,
    {
      id,
      kind: options.kind ?? 'info',
      message,
      actionLabel: options.actionLabel ?? null,
      onAction: options.onAction ?? null,
    },
  ]
  const duration = options.durationMs ?? (options.onAction ? 8000 : 5000)
  setTimeout(() => dismiss(id), duration)
  return id
}

export function useGalleryToast(): {
  toasts: Ref<GalleryToast[]>
  push: (message: string, options?: GalleryToastOptions) => number
  dismiss: (id: number) => void
} {
  return { toasts, push, dismiss }
}
